import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Candidate, CandidateCareerField, CandidateSector } from '~/services/CandidateService';
import { User } from '~/services/IdentityService';
import {
    CandidateJobBuilderEventData,
    CandidateJobBuilderEventHandler,
    CandidateJobBuilderEventListener,
    CandidateJobBuilderEventType,
    CandidateJobBuilderInit,
    CandidateOperation,
    CandidateUpdateProfileJob,
    CSPOperation,
    ExcludeId,
    JobCandidate,
    JobCSP,
    JobLanguage,
    JobLink,
    JobMembership,
    JobNationality,
    JobPreferences,
    JobSkill,
    JobUser,
    JobWorkAuthorization,
    JobWorkExperience,
    LanguageOperation,
    LinksOperation,
    MembershipOperation,
    PreferencesOperation,
    SkillsOperation,
    UserOperation,
    WorkAuthorizationOperation,
    WorkExperienceOperation,
} from './CandidateJobBuilder.types';
import {
    createListOperations,
    extract,
    ObjectWithId,
    TEMP_ID_PREFIX,
    TEMP_ID_VALUE_DELIMITER,
} from './CandidateJobBuilder.util';

function watcher(_: CandidateJobBuilder, prop: string, descriptor: PropertyDescriptor) {
    return {
        get() {
            function wrapper(this: CandidateJobBuilder, ...args: any[]) {
                const callback = descriptor.value.apply(this, args);
                const self = this;
                return (value: any) => {
                    const result = callback(value);

                    const empty = self.isEmpty();
                    self.dispatchEvent('change', { value, empty });

                    return result;
                };
            }
            Object.defineProperty(this, prop, {
                value: wrapper,
                configurable: true,
                writable: true,
            });
            return wrapper;
        },
    };
}

export class CandidateJobBuilder {
    // @ts-ignore
    private user: JobUser;
    private originalUser: Partial<JobUser> = {};
    private userOperation: UserOperation = {
        op: 'update',
        value: {},
    };

    // @ts-ignore
    private candidate: JobCandidate;
    private originalCandidate: Partial<JobCandidate> = {};
    private candidateOperation: CandidateOperation = {
        op: 'update',
        value: {},
    };

    // @ts-ignore
    private workAuthorizations: JobWorkAuthorization[];
    private originalWorkAuthorizations: JobWorkAuthorization[] = [];
    private workAuthorizationsOperations: WorkAuthorizationOperation[] = [];

    // @ts-ignore
    private languages: JobLanguage[];
    private originalLanguages: JobLanguage[] = [];
    private languagesOperations: LanguageOperation[] = [];

    // @ts-ignore
    private workExperiences: JobWorkExperience[];
    private originalWorkExperiences: JobWorkExperience[] = [];
    private workExperiencesOperations: WorkExperienceOperation[] = [];

    // @ts-ignore
    private memberships: JobMembership[];
    private originalMemberships: JobMembership[] = [];
    private membershipOperations: MembershipOperation[] = [];

    // @ts-ignore
    private nationalities: JobNationality[];
    private originalNationalities: JobNationality[] = [];
    private nationalitiesOperations: LanguageOperation[] = [];

    // @ts-ignore
    private skills: JobSkill[];
    private originalSkills: JobSkill[] = [];
    private skillsOperation: SkillsOperation = {
        op: 'update',
        value: {},
    };

    // @ts-ignore
    private links: JobLink[];
    private originalLinks: JobLink[] = [];
    private linksOperations: LinksOperation[] = [];

    // @ts-ignore
    private preferences: JobPreferences;
    private originalPreferences: Partial<JobPreferences> = {};
    private preferencesOperation: PreferencesOperation = {
        op: 'update',
        value: {},
    };

    // @ts-ignore
    private csp: JobCSP[];
    private originalCSP: JobCSP[] = [];
    private cspOperations: CSPOperation[] = [];

    private listeners: CandidateJobBuilderEventListener<any>[] = [];

    constructor(config: CandidateJobBuilderInit) {
        this.init(config);
    }

    init(config: CandidateJobBuilderInit, updateOriginals = false) {
        this.user = extract(
            config.user,
            'email',
            'phoneNumber',
            'avatar',
            'firstName',
            'lastName',
            'newsLetterConsent'
        );
        this.candidate = extract(
            config.candidate,
            'gender',
            'pronoun',
            'country',
            'summary',
            'profileVisibility',
            'ethnicMinority',
            'disability',
            'newsletterConsent'
        );
        this.workAuthorizations = extract(
            config.workAuthorizations.filter(wa => !wa.implicit),
            'type',
            'code'
        );
        this.languages = extract(config.candidate.languages, 'languageId', 'proficiencyId');
        this.workExperiences = extract(
            config.candidate.workExperiences,
            'title',
            'companyTitle',
            'location',
            'description',
            'start',
            'end',
            'isCurrentJob'
        );
        this.memberships = extract(
            config.candidate.memberships,
            'title',
            'organizationName',
            'start',
            'end',
            'description',
            'isActive'
        );

        this.nationalities = extract(config.candidate.nationalities, 'nationality');
        this.skills = extract(config.candidate.skills, 'skillId', 'customSkillName');
        this.links = extract(config.candidate.links, 'name', 'value');
        this.preferences = extract(
            config.candidate.preferences,
            'preferredEmployers',
            'eligibleForRelocation',
            'memberOfLGBTQCommunity',
            'eligibleWorkLocation',
            'workPreferences',
            'sectors',
            'careerFields'
        );
        this.csp = config.candidate.studyPrograms.map(program => ({
            ...extract(program, 'programId', 'campusId', 'start', 'end'),
            studyProgram: !program.studyProgram
                ? undefined
                : extract(
                      program.studyProgram,
                      'name',
                      'customSchoolName',
                      'schoolId',
                      'levelId',
                      'fields',
                      'isVerified'
                  ),
        }));

        if (updateOriginals) this.updateOriginals();
    }

    /* Events */

    /** Add an event handler */
    on<E extends CandidateJobBuilderEventType>(
        eventType: E,
        handler: CandidateJobBuilderEventHandler[E]
    ) {
        this.listeners.push({
            event: eventType,
            handler,
        });
    }
    /** Remove an event handler */
    off<E extends CandidateJobBuilderEventType>(
        eventType: E,
        handler: CandidateJobBuilderEventHandler[E]
    ) {
        this.listeners = this.listeners.filter(
            listener => listener.event !== eventType || listener.handler !== handler
        );
    }
    /** Dispatch an event */
    dispatchEvent<E extends CandidateJobBuilderEventType>(
        eventType: E,
        data: CandidateJobBuilderEventData[E]
    ) {
        for (const listener of this.listeners)
            if (listener.event === eventType) listener.handler({ eventType, ...data });
    }

    /* Getters and watchers */

    getUserField<T extends keyof JobUser>(field: T) {
        return cloneDeep(this.user[field]);
    }
    @watcher
    watchUser<T extends ExcludeId<UserOperation['value']>>(field: T) {
        this.originalUser[field] = this.user[field];

        return (value: User[T]) => {
            if (this.originalUser[field] === value) {
                delete this.userOperation.value[field];
                return;
            }

            this.userOperation.value[field] = value;
        };
    }

    getCandidateField<T extends keyof JobCandidate>(field: T) {
        return cloneDeep(this.candidate[field]);
    }

    getCandidateOperation(): Partial<JobCandidate> {
        return cloneDeep(this.candidateOperation.value);
    }

    removeCandidateFieldFromOp<T extends keyof JobCandidate>(field: T) {
        delete this.candidateOperation.value[field];
    }

    @watcher
    watchCandidate<T extends ExcludeId<CandidateOperation['value']>>(field: T) {
        this.originalCandidate[field] = this.candidate[field];

        return (value: Candidate[T]) => {
            if (isEqual(this.originalCandidate[field], value)) {
                delete this.candidateOperation.value[field];
                return;
            }

            this.candidateOperation.value[field] = value;
        };
    }

    getWorkAuthorizations() {
        return cloneDeep(this.workAuthorizations);
    }
    @watcher
    watchWorkAuthorizations() {
        this.originalWorkAuthorizations = [...this.workAuthorizations];

        return (current: JobWorkAuthorization[]) => {
            this.workAuthorizationsOperations = createListOperations(
                current,
                this.originalWorkAuthorizations,
                'code',
                'type'
            );
        };
    }

    getLanguages() {
        return cloneDeep(this.languages);
    }
    @watcher
    watchLanguages() {
        this.originalLanguages = [...this.languages];

        return (current: JobLanguage[]) => {
            this.languagesOperations = createListOperations(
                current.filter(lang => lang.proficiencyId && lang.languageId),
                this.originalLanguages,
                'languageId',
                'proficiencyId'
            );
        };
    }

    getWorkExperiences() {
        return this.workExperiences;
    }
    @watcher
    watchWorkExperiences() {
        this.originalWorkExperiences = [...this.workExperiences];

        return (current: JobWorkExperience[]) => {
            this.workExperiencesOperations = createListOperations(
                current,
                this.originalWorkExperiences,
                'title',
                'companyTitle',
                'description',
                'start',
                'end',
                'location',
                'isCurrentJob'
            );
        };
    }

    getMemberships() {
        return this.memberships;
    }
    @watcher
    watchMemberships() {
        this.originalMemberships = [...this.memberships];

        return (current: JobMembership[]) => {
            this.membershipOperations = createListOperations(
                current,
                this.originalMemberships,
                'title',
                'organizationName',
                'start',
                'end'
            );
        };
    }

    getNationalities() {
        return cloneDeep(this.nationalities);
    }
    @watcher
    watchNationalities() {
        this.originalNationalities = [...this.nationalities];

        return (current: JobNationality[]) => {
            this.nationalitiesOperations = createListOperations(
                current,
                this.originalNationalities,
                'nationality'
            );
        };
    }

    getSkills() {
        return cloneDeep(this.skills);
    }
    @watcher
    watchSkills() {
        this.originalSkills = [...this.skills];

        return (current: JobSkill[]) => {
            const addSkills = current.reduce<
                Exclude<SkillsOperation['value']['addSkills'], undefined>
            >((acc, skill) => {
                if (!this.originalSkills.some(s => s.id === skill.id))
                    if (!!skill.skillId) acc.push({ skillId: skill.skillId });
                    else if (!!skill.customSkillName)
                        acc.push({ customSkillName: skill.customSkillName });
                return acc;
            }, []);
            const removeSkills = this.originalSkills.reduce<string[]>((acc, skill) => {
                if (!current.some(s => s.id === skill.id)) acc.push(skill.id);
                return acc;
            }, []);

            this.skillsOperation.value.addSkills = !addSkills.length ? undefined : addSkills;
            this.skillsOperation.value.removeSkills = !removeSkills.length
                ? undefined
                : removeSkills;
        };
    }

    getLinks() {
        return cloneDeep(this.links);
    }
    @watcher
    watchLinks() {
        this.originalLinks = [...this.links];

        return (current: JobLink[]) => {
            this.linksOperations = createListOperations(
                current,
                this.originalLinks,
                'name',
                'value'
            );
        };
    }

    getPreferencesField<T extends keyof JobPreferences>(field: T) {
        return cloneDeep(this.preferences[field]);
    }
    @watcher
    watchPreference<T extends keyof JobPreferences>(field: T) {
        this.originalPreferences[field] = this.preferences[field];

        return (value: PreferencesOperation['value'][T]) => {
            if (isEqual(this.originalPreferences[field], value)) {
                delete this.preferencesOperation.value[field];
                return;
            }

            if (field === 'sectors') {
                const sectors: CandidateSector[] = (value as CandidateSector[]) || [];

                const addSectors = sectors.reduce<
                    Exclude<PreferencesOperation['value']['addPreferredSectors'], undefined>
                >((acc, sector) => {
                    if (
                        this.originalPreferences.sectors &&
                        !this.originalPreferences.sectors.some(s => s.sectorId === sector.sectorId)
                    ) {
                        acc.push(sector.sectorId);
                    }
                    return acc;
                }, []);
                const removeSectors = (this.originalPreferences.sectors || []).reduce<string[]>(
                    (acc, sector) => {
                        if (!sectors.some(s => s.sectorId === sector.sectorId))
                            acc.push(sector.sectorId);
                        return acc;
                    },
                    []
                );
                this.preferencesOperation.value.addPreferredSectors = !addSectors.length
                    ? undefined
                    : addSectors;
                this.preferencesOperation.value.removePreferredSectors = !removeSectors.length
                    ? undefined
                    : removeSectors;
            } else if (field === 'careerFields') {
                const careerFields: CandidateCareerField[] =
                    (value as CandidateCareerField[]) || [];

                const addCareerFields = careerFields.reduce<
                    Exclude<PreferencesOperation['value']['addPreferredCareerFields'], undefined>
                >((acc, careerField) => {
                    if (
                        this.originalPreferences.careerFields &&
                        !this.originalPreferences.careerFields.some(
                            cf => cf.careerFieldId === careerField.careerFieldId
                        )
                    ) {
                        acc.push(careerField.careerFieldId);
                    }
                    return acc;
                }, []);
                const removeCareerFields = (this.originalPreferences.careerFields || []).reduce<
                    string[]
                >((acc, careerField) => {
                    if (!careerFields.some(s => s.careerFieldId === careerField.careerFieldId))
                        acc.push(careerField.careerFieldId);
                    return acc;
                }, []);
                this.preferencesOperation.value.addPreferredCareerFields = !addCareerFields.length
                    ? undefined
                    : addCareerFields;
                this.preferencesOperation.value.removePreferredCareerFields = !removeCareerFields.length
                    ? undefined
                    : removeCareerFields;
            } else if (field === 'preferredEmployers') {
                // Split between custom employers and existing employers
                const preferredEmployers = (value as string[]) || [];

                const customPreferredEmployers = preferredEmployers
                    .map(id => {
                        const values = CandidateJobBuilder.getValuesFromTempId(id);
                        if (!values) return null;
                        return {
                            name: values[0],
                            order: Number.parseInt(values[1], 10),
                        };
                    })
                    .filter(value => value !== null) as {
                    name: string;
                    order: number;
                }[];
                const addPreferredEmployers = preferredEmployers.filter(
                    employerId => !CandidateJobBuilder.isTempItemWithValue({ id: employerId })
                );
                this.preferencesOperation.value.preferredEmployers = addPreferredEmployers;
                this.preferencesOperation.value.addCustomEmployers = customPreferredEmployers;
            } else {
                this.preferencesOperation.value[field] = value;
            }
        };
    }

    getCSPs() {
        return cloneDeep(this.csp);
    }
    @watcher
    watchCSPs() {
        this.originalCSP = cloneDeep(this.csp);

        return (current: JobCSP[]) => {
            const ops = createListOperations<CSPOperation, JobCSP>(
                current,
                this.originalCSP,
                'programId',
                'campusId',
                'start',
                'end',
                'studyProgram'
            );

            // Take care of fields
            for (const op of ops) {
                if (!op.value.studyProgram) continue;

                op.value.studyProgram = cloneDeep(op.value.studyProgram);

                if (op.op === 'update') {
                    const mapSort = (arr: ObjectWithId[]) =>
                        arr.map(f => f.id).sort((a, b) => a.localeCompare(b));

                    const orig = this.originalCSP.find(c => c.id === op.value.id);
                    if (!orig || !orig.studyProgram) continue;
                    const origFields = mapSort(orig.studyProgram.fields || []);

                    const cur = current.find(c => c.id === op.value.id);
                    if (!cur || !cur.studyProgram) continue;
                    const curFields = mapSort(cur.studyProgram.fields || []);

                    if (isEqual(origFields, curFields)) continue;

                    const addFields = curFields.filter(f => !origFields.includes(f));
                    const removeFields = origFields.filter(f => !curFields.includes(f));

                    op.value.studyProgram.addFields = addFields;
                    op.value.studyProgram.removeFields = removeFields;

                    delete op.value.studyProgram.fields;
                } else if (op.op === 'create') {
                    const fields = op.value.studyProgram.fields;
                    if (!fields || !fields.length) continue;

                    op.value.studyProgram.addFields = fields.map(f => f.id);
                    delete op.value.studyProgram.fields;
                }
            }

            this.cspOperations = ops;
        };
    }

    /* Utility */

    get hasUserOp() {
        return !!Object.keys(this.userOperation.value).length;
    }
    get hasCandidateOp() {
        return !!Object.keys(this.candidateOperation.value).length;
    }
    get hasWorkAuthorizationsOp() {
        return !!this.workAuthorizationsOperations.length;
    }
    get hasLanguagesOp() {
        return !!this.languagesOperations.length;
    }
    get hasWorkExperiencesOp() {
        return !!this.workExperiencesOperations.length;
    }
    get hasMembershipsOp() {
        return !!this.membershipOperations.length;
    }
    get hasNationalitiesOp() {
        return !!this.nationalitiesOperations.length;
    }
    get hasSkillsOp() {
        return (
            (this.skillsOperation.value.addSkills && this.skillsOperation.value.addSkills.length) ||
            (this.skillsOperation.value.removeSkills &&
                this.skillsOperation.value.removeSkills.length)
        );
    }
    get hasLinksOp() {
        return !!this.linksOperations.length;
    }
    get hasPreferencesOp() {
        return !!Object.keys(this.preferencesOperation.value).length;
    }
    get hasCSPOp() {
        return !!this.cspOperations.length;
    }

    get userId() {
        return this.user.id;
    }
    get candidateId() {
        return this.candidate.id;
    }

    /** Returns true if there are no operations to perform */
    isEmpty() {
        return !(
            this.hasUserOp ||
            this.hasCandidateOp ||
            this.hasWorkAuthorizationsOp ||
            this.hasLanguagesOp ||
            this.hasWorkExperiencesOp ||
            this.hasMembershipsOp ||
            this.hasNationalitiesOp ||
            this.hasSkillsOp ||
            this.hasLinksOp ||
            this.hasPreferencesOp ||
            this.hasCSPOp
        );
    }

    /** Clear all operations */
    reset() {
        this.userOperation = {
            op: 'update',
            value: {},
        };
        this.candidateOperation = {
            op: 'update',
            value: {},
        };
        this.workAuthorizationsOperations = [];
        this.languagesOperations = [];
        this.workExperiencesOperations = [];
        this.membershipOperations = [];
        this.nationalitiesOperations = [];
        this.skillsOperation = {
            op: 'update',
            value: {},
        };
        this.linksOperations = [];
        this.preferencesOperation = {
            op: 'update',
            value: {},
        };
        this.cspOperations = [];

        if (!this.isEmpty())
            throw new Error(
                "All operations not removed after reset(), this is a bug. Please check that you've cleared everything."
            );

        this.dispatchEvent('change', { empty: true });
    }

    updateOriginals() {
        this.originalUser = cloneDeep(this.user);
        this.originalCandidate = cloneDeep(this.candidate);
        this.originalWorkAuthorizations = cloneDeep(this.workAuthorizations);
        this.originalLanguages = cloneDeep(this.languages);
        this.originalWorkExperiences = cloneDeep(this.workExperiences);
        this.originalMemberships = cloneDeep(this.memberships);
        this.originalNationalities = cloneDeep(this.nationalities);
        this.originalSkills = cloneDeep(this.skills);
        this.originalLinks = cloneDeep(this.links);
        this.originalPreferences = cloneDeep(this.preferences);
        this.originalCSP = cloneDeep(this.csp);
    }

    buildRequest(): CandidateUpdateProfileJob {
        const job: CandidateUpdateProfileJob = {
            userId: this.user.id,
        };

        if (this.hasUserOp) job.user = this.userOperation;

        if (this.hasCandidateOp) job.candidate = this.candidateOperation;

        if (this.hasWorkAuthorizationsOp)
            job.workAuthorizations = this.workAuthorizationsOperations;

        if (this.hasLanguagesOp) job.languages = this.languagesOperations;

        if (this.hasWorkExperiencesOp) job.workExperiences = this.workExperiencesOperations;

        if (this.hasMembershipsOp) job.memberships = this.membershipOperations;

        if (this.hasNationalitiesOp) job.nationalities = this.nationalitiesOperations;

        if (this.hasSkillsOp) job.skills = this.skillsOperation;

        if (this.hasLinksOp) job.links = this.linksOperations;

        if (this.hasPreferencesOp) job.preferences = this.preferencesOperation;

        if (this.hasCSPOp) job.studyPrograms = this.cspOperations;

        return job;
    }

    /* Statics */

    /** Generate random ID with configured prefix */
    static generateId() {
        return (
            TEMP_ID_PREFIX +
            Math.random()
                .toString(36)
                .substring(2)
        );
    }

    static generateIdWithValues(value: string[]) {
        const id = CandidateJobBuilder.generateId();
        return id.concat(TEMP_ID_VALUE_DELIMITER, value.join(TEMP_ID_VALUE_DELIMITER));
    }

    /** Check if item id starts with configured prefix */
    static isTempItem(item: { id: string }) {
        return item.id.startsWith(TEMP_ID_PREFIX);
    }

    static isTempItemWithValue(item: { id: string }) {
        return CandidateJobBuilder.isTempItem(item) && item.id.includes(TEMP_ID_VALUE_DELIMITER);
    }

    static getValuesFromTempId(id: string): string[] | null {
        if (!CandidateJobBuilder.isTempItemWithValue({ id })) {
            return null;
        }
        return id.split(TEMP_ID_VALUE_DELIMITER).slice(1);
    }
}
